import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Phone, Loader2, Save, Play, ShieldCheck, PhoneForwarded, Circle, RefreshCw, Hash, Workflow, HeartPulse, CheckCircle2, AlertTriangle, Users, Plus, Trash2, Pencil, Activity } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import RecordingPlayerDialog from '@/components/voice/RecordingPlayerDialog';
import { voiceAPI, teamAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const ROLE_OPTIONS = [
  { value: 'Manager', label: 'Managers' },
  { value: 'Accounting', label: 'Accounting' },
  { value: 'Agency_Principal', label: 'Agency Principal' },
  { value: 'Agency_User', label: 'Agency Admin / Marketing' },
  { value: 'Agent', label: 'Agents' },
];

export default function VoiceSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [tab, setTab] = useState('overview');
  const [assignAgents, setAssignAgents] = useState([]);
  const [calls, setCalls] = useState([]);
  const [playerSid, setPlayerSid] = useState(null);
  const [callQuery, setCallQuery] = useState('');
  const [recordingsOnly, setRecordingsOnly] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [online, setOnline] = useState([]);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try { const r = await voiceAPI.online(); if (!stop) setOnline(r.data.items || []); } catch (_) { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const searchCalls = useCallback(async () => {
    try {
      const params = {};
      if (callQuery) params.q = callQuery;
      if (recordingsOnly) params.recordings_only = true;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const c = await voiceAPI.calls(params);
      setCalls(c.data.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Search failed'));
    }
  }, [callQuery, recordingsOnly, fromDate, toDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await voiceAPI.settings();
      setSettings(s.data);
      const [c, t] = await Promise.all([
        voiceAPI.calls().catch(() => ({ data: { items: [] } })),
        teamAPI.list().catch(() => ({ data: [] })),
      ]);
      setCalls(c.data.items || []);
      voiceAPI.assignableAgents().then((r) => setAssignAgents(r.data.items || [])).catch(() => {});
      const rawT = Array.isArray(t.data) ? t.data : (t.data.items || t.data.members || []);
      setAdmins(rawT.filter((m) => (m.roles || []).some((r) => String(r.role || r).includes('KR_'))));
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Calling is not enabled for your account.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        default_caller_number: settings.default_caller_number,
        forward_enabled: settings.forward_enabled,
        forward_number: settings.forward_number,
        inbound_ring_softphone: settings.inbound_ring_softphone,
        smart_caller_routing: settings.smart_caller_routing,
        announcement: settings.announcement,
        recording_enabled: settings.recording_enabled,
        recording_access_user_ids: settings.recording_access_user_ids || [],
        pause_recording_enabled: settings.pause_recording_enabled,
        pause_recording_user_ids: settings.pause_recording_user_ids || [],
        pause_recording_roles: settings.pause_recording_roles || [],
        pause_recording_team_ids: settings.pause_recording_team_ids || [],
        softphone_roles: settings.softphone_roles || [],
        softphone_user_ids: settings.softphone_user_ids || [],
        comm_log_roles: settings.comm_log_roles || [],
        voicemail_enabled: settings.voicemail_enabled,
        voicemail_greeting: settings.voicemail_greeting,
        business_hours_enabled: settings.business_hours_enabled,
        business_timezone: settings.business_timezone,
        business_hours_start: settings.business_hours_start,
        business_hours_end: settings.business_hours_end,
        business_days: settings.business_days || [],
        ivr_enabled: settings.ivr_enabled,
        ivr_greeting: settings.ivr_greeting,
        ivr_greeting_es: settings.ivr_greeting_es,
        ivr_bilingual: settings.ivr_bilingual,
        ivr_lang_prompt: settings.ivr_lang_prompt,
        ivr_options: settings.ivr_options || [],
        agent_numbers: settings.agent_numbers || [],
        number_nicknames: settings.number_nicknames || {},
        disposition_required: settings.disposition_required,
      };
      const r = await voiceAPI.updateSettings(payload);
      setSettings((s) => ({ ...s, ...r.data }));
      toast.success('Settings saved');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const provision = async () => {
    setProvisioning(true);
    try {
      const r = await voiceAPI.provision();
      toast.success('Calling set up successfully');
      if (r.data.warnings?.length) toast.warning(r.data.warnings.join(' · '));
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Setup failed'));
    } finally {
      setProvisioning(false);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncNumbers = async () => {
    setSyncing(true);
    try {
      const r = await voiceAPI.syncNumbers();
      const added = r.data.added?.length || 0;
      toast.success(added ? `${added} new number${added === 1 ? '' : 's'} synced from Twilio` : 'Numbers are up to date');
      if (r.data.warnings?.length) toast.warning(r.data.warnings.join(' · '));
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not sync numbers'));
    } finally {
      setSyncing(false);
    }
  };

  const setNickname = (num, val) => {
    setSettings((s) => ({ ...s, number_nicknames: { ...(s.number_nicknames || {}), [num]: val } }));
  };
  const labelFor = (num) => {
    const nick = (settings?.number_nicknames || {})[num];
    return nick && nick !== num ? `${nick} (${num})` : num;
  };

  const toggleAdmin = (uid) => {
    setSettings((s) => {
      const cur = new Set(s.recording_access_user_ids || []);
      if (cur.has(uid)) cur.delete(uid); else cur.add(uid);
      return { ...s, recording_access_user_ids: Array.from(cur) };
    });
  };

  const toggleRole = (field, value) => {
    setSettings((s) => {
      const cur = new Set(s[field] || []);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      return { ...s, [field]: Array.from(cur) };
    });
  };

  if (loading || !settings) {
    if (!loading && loadError) {
      return (
        <div data-testid="voice-settings-page">
          <PageHeader title="Calling" description="Browser softphone & call history." />
          <div className="page-content max-w-xl">
            <Card>
              <CardContent className="p-6 text-center text-sm text-slate-600" data-testid="voice-no-access">
                {loadError}
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="animate-spin text-slate-400" /></div>;
  }

  const canManage = settings.can_manage;

  // Restricted agents (no admin rights) get a focused "My Call History" view —
  // only their own calls, no admin configuration cards.
  if (settings.restricted) {
    return (
      <div data-testid="voice-settings-page">
        <PageHeader
          title="My Call History"
          description="Calls on your number and from your assigned leads & clients — so you never miss a callback."
          breadcrumbs={[{ label: 'Calling', href: '/settings/voice' }]}
        />
        <div className="page-content space-y-5 max-w-4xl">
          {settings.default_caller_number && (
            <Card>
              <CardContent className="p-4 flex items-center gap-2 text-sm">
                <Phone size={16} className="text-slate-500" />
                <span className="text-slate-500">Your number:</span>
                <span className="font-semibold text-slate-800">{settings.default_caller_number}</span>
                {settings.using_general_leads_number && (
                  <Badge className="bg-amber-100 text-amber-800">General Leads Number</Badge>
                )}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader><CardTitle className="text-base">Call history</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-2 mb-4">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search number</Label>
                  <Input value={callQuery} onChange={(e) => setCallQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchCalls()} placeholder="+1863…" className="h-9 text-sm" data-testid="agent-call-search-number" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
                  <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 text-sm" data-testid="agent-call-search-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
                  <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 text-sm" data-testid="agent-call-search-to" />
                </div>
                <Button size="sm" onClick={searchCalls} data-testid="agent-call-search-btn">Search</Button>
              </div>
              {calls.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No calls yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr><th className="px-3 py-2 text-left">Direction</th><th className="px-3 py-2 text-left">From</th><th className="px-3 py-2 text-left">To</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">When</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {calls.map((c) => (
                        <tr key={c.call_sid} data-testid={`agent-call-row-${c.call_sid}`}>
                          <td className="px-3 py-2"><Badge className={c.direction === 'inbound' ? 'bg-sky-100 text-sky-800' : 'bg-indigo-100 text-indigo-800'}>{c.direction}</Badge></td>
                          <td className="px-3 py-2">
                            {c.from_name && <div className="text-xs font-medium text-slate-800">{c.from_name}</div>}
                            <div className="font-mono text-xs text-slate-500">{c.from_number || '—'}</div>
                          </td>
                          <td className="px-3 py-2">
                            {c.to_name && <div className="text-xs font-medium text-slate-800">{c.to_name}</div>}
                            <div className="font-mono text-xs text-slate-500">{c.to_number || (c.forwarded_to ? `→ ${c.forwarded_to}` : '—')}</div>
                          </td>
                          <td className="px-3 py-2 text-xs">{c.status}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{c.started_at ? new Date(c.started_at).toLocaleString() : (c.created_at ? new Date(c.created_at).toLocaleString() : '—')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="voice-settings-page">
      <PageHeader
        title="Calling (Twilio Voice)"
        description="Browser softphone for KR admins — make and receive calls without a personal number."
        breadcrumbs={[{ label: 'Settings', href: '/platform-settings' }, { label: 'Calling', href: '/settings/voice' }]}
      />
      <div className="page-content space-y-5 max-w-4xl">
        {!settings.configured && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-5 flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-amber-900 flex items-center gap-2"><Phone size={16} /> Calling is not set up yet</h3>
                <p className="text-sm text-amber-800 mt-1">Run one-time setup to provision the Twilio softphone and point your numbers at the CRM.</p>
              </div>
              {canManage ? (
                <Button onClick={provision} disabled={provisioning} data-testid="voice-provision-btn">
                  {provisioning ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShieldCheck size={14} className="mr-1" />}
                  Set up calling
                </Button>
              ) : (
                <Badge className="bg-amber-100 text-amber-800">Ask a KR SuperAdmin to set up</Badge>
              )}
            </CardContent>
          </Card>
        )}

        {settings.configured && (
          <Badge className="bg-emerald-100 text-emerald-800" data-testid="voice-configured-badge">
            <Circle size={8} className="mr-1 fill-emerald-500 text-emerald-500" /> Softphone active
          </Badge>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="voice-tabs">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100/70 p-1 rounded-xl mb-5">
            <TabsTrigger value="overview" className="data-[state=active]:bg-white data-[state=active]:text-[#0f2547] data-[state=active]:shadow-sm" data-testid="voice-tab-overview"><Activity size={14} className="mr-1.5" /> Overview</TabsTrigger>
            <TabsTrigger value="numbers" className="data-[state=active]:bg-white data-[state=active]:text-[#0f2547] data-[state=active]:shadow-sm" data-testid="voice-tab-numbers"><Hash size={14} className="mr-1.5" /> Numbers</TabsTrigger>
            <TabsTrigger value="routing" className="data-[state=active]:bg-white data-[state=active]:text-[#0f2547] data-[state=active]:shadow-sm" data-testid="voice-tab-routing"><PhoneForwarded size={14} className="mr-1.5" /> Routing &amp; Hours</TabsTrigger>
            <TabsTrigger value="agents" className="data-[state=active]:bg-white data-[state=active]:text-[#0f2547] data-[state=active]:shadow-sm" data-testid="voice-tab-agents"><Users size={14} className="mr-1.5" /> IVR &amp; Access</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-[#0f2547] data-[state=active]:shadow-sm" data-testid="voice-tab-history"><Phone size={14} className="mr-1.5" /> Call History</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="space-y-5 mt-0" data-testid="voice-tabcontent-overview">

        <Card className="border-[#0f2547]/20 bg-gradient-to-r from-slate-50 to-white" data-testid="voice-callflows-card">
          <CardContent className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#0f2547] text-white flex items-center justify-center"><Workflow size={20} /></div>
              <div>
                <h3 className="font-semibold text-slate-900">Call Flows</h3>
                <p className="text-sm text-slate-600 mt-0.5">Design a visual call flow per phone number — IVR menus, teams, business hours and more. Numbers without a flow keep today's behavior.</p>
              </div>
            </div>
            <Button onClick={() => navigate('/settings/voice/flows')} className="bg-[#0f2547] shrink-0" data-testid="voice-open-callflows">Open builder</Button>
          </CardContent>
        </Card>

        <Card data-testid="voice-online-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Circle size={10} className="fill-emerald-500 text-emerald-500" /> Online now — ready to receive calls</CardTitle></CardHeader>
          <CardContent>
            {online.length === 0 ? (
              <p className="text-sm text-slate-500">No admins are online right now. Inbound calls will forward or go to voicemail.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {online.map((o) => (
                  <span key={o.user_id} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`voice-online-${o.user_id}`}>
                    <Circle size={7} className="fill-emerald-500 text-emerald-500" /> {o.name || o.user_id}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

          </TabsContent>
          <TabsContent value="numbers" className="space-y-5 mt-0" data-testid="voice-tabcontent-numbers">

        <Card data-testid="voice-numbers-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Hash size={16} /> Phone numbers</CardTitle>
            {canManage && (
              <Button size="sm" variant="outline" onClick={syncNumbers} disabled={syncing} data-testid="voice-sync-btn">
                {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
                Sync from Twilio
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-[11px] text-slate-400">Give each number a nickname. Click "Sync from Twilio" to pull in numbers you added on your Twilio account.</p>
            {(settings.caller_numbers || []).length === 0 && <p className="text-sm text-slate-500">No numbers yet — sync from Twilio.</p>}
            {(settings.caller_numbers || []).map((n) => (
              <div key={n} className="flex items-center gap-3">
                <span className="font-mono text-sm text-slate-700 w-40 shrink-0">{n}</span>
                <Input
                  value={(settings.number_nicknames || {})[n] || ''}
                  onChange={(e) => setNickname(n, e.target.value)}
                  placeholder="Nickname (e.g. Sales Line)"
                  className="h-9 text-sm"
                  disabled={!canManage}
                  data-testid={`voice-nickname-${n}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <NumberHealthCard canManage={canManage} />

          </TabsContent>
          <TabsContent value="routing" className="space-y-5 mt-0" data-testid="voice-tabcontent-routing">

        <TeamsCard canManage={canManage} onTeamsChange={(teams) => setSettings((s) => ({ ...s, ring_groups: teams }))} />

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><PhoneForwarded size={16} /> Call routing</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Outbound caller ID</Label>
                <Select value={settings.default_caller_number} onValueChange={(v) => set('default_caller_number', v)} disabled={!canManage}>
                  <SelectTrigger className="mt-1" data-testid="voice-caller-number"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(settings.caller_numbers || []).map((n) => <SelectItem key={n} value={n}>{labelFor(n)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400 mt-1">The number shown to the person you call.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Forward number (no answer)</Label>
                <Input className="mt-1" value={settings.forward_number || ''} onChange={(e) => set('forward_number', e.target.value)} placeholder="+18135551212" disabled={!canManage} data-testid="voice-forward-number" />
                <p className="text-[11px] text-slate-400 mt-1">Where inbound calls go if nobody answers in the CRM.</p>
              </div>
            </div>
            <ToggleRow label="Ring the CRM softphone on inbound calls" checked={settings.inbound_ring_softphone} onChange={(v) => set('inbound_ring_softphone', v)} disabled={!canManage} testId="voice-ring-toggle" />
            <ToggleRow label="Smart routing: send a known lead/client's call to their assigned agent first" checked={settings.smart_caller_routing !== false} onChange={(v) => set('smart_caller_routing', v)} disabled={!canManage} testId="voice-smart-routing-toggle" />
            <ToggleRow label="Forward to the number above when nobody answers" checked={settings.forward_enabled} onChange={(v) => set('forward_enabled', v)} disabled={!canManage} testId="voice-forward-toggle" />
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Announcement before forwarding</Label>
              <Input className="mt-1" value={settings.announcement || ''} onChange={(e) => set('announcement', e.target.value)} disabled={!canManage} data-testid="voice-announcement" />
            </div>
            <ToggleRow label="Take a voicemail when nobody answers and there is no forward number" checked={settings.voicemail_enabled} onChange={(v) => set('voicemail_enabled', v)} disabled={!canManage} testId="voice-voicemail-toggle" />
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Voicemail greeting</Label>
              <Textarea className="mt-1" rows={2} value={settings.voicemail_greeting || ''} onChange={(e) => set('voicemail_greeting', e.target.value)} disabled={!canManage || !settings.voicemail_enabled} data-testid="voice-voicemail-greeting" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Circle size={14} className="fill-sky-500 text-sky-500" /> Business hours</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <ToggleRow
              label="Only ring/forward during business hours (outside hours → voicemail)"
              checked={!!settings.business_hours_enabled}
              onChange={(v) => set('business_hours_enabled', v)}
              disabled={!canManage}
              testId="voice-business-hours-toggle"
            />
            <div className={settings.business_hours_enabled ? '' : 'opacity-50 pointer-events-none'}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Open from</Label>
                  <Input type="time" className="mt-1" value={settings.business_hours_start || '09:00'} onChange={(e) => set('business_hours_start', e.target.value)} disabled={!canManage} data-testid="voice-bh-start" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Open until</Label>
                  <Input type="time" className="mt-1" value={settings.business_hours_end || '18:00'} onChange={(e) => set('business_hours_end', e.target.value)} disabled={!canManage} data-testid="voice-bh-end" />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Timezone</Label>
                <select
                  className="w-full mt-1 text-sm border border-slate-200 rounded-lg py-2 px-2 bg-white"
                  value={settings.business_timezone || 'America/New_York'}
                  onChange={(e) => set('business_timezone', e.target.value)}
                  disabled={!canManage}
                  data-testid="voice-bh-timezone"
                >
                  {[
                    ['America/New_York', 'Eastern (New York)'],
                    ['America/Puerto_Rico', 'Puerto Rico (Atlantic)'],
                    ['America/Chicago', 'Central (Chicago)'],
                    ['America/Denver', 'Mountain (Denver)'],
                    ['America/Phoenix', 'Arizona (Phoenix)'],
                    ['America/Los_Angeles', 'Pacific (Los Angeles)'],
                  ].map(([tz, label]) => <option key={tz} value={tz}>{label}</option>)}
                </select>
              </div>
              <div className="mt-3">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Days open</Label>
                <div className="flex gap-1.5 mt-1" data-testid="voice-bh-days">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => {
                    const days = settings.business_days || [0, 1, 2, 3, 4];
                    const on = days.includes(i);
                    return (
                      <button
                        key={d}
                        type="button"
                        disabled={!canManage}
                        onClick={() => {
                          const next = on ? days.filter((x) => x !== i) : [...days, i].sort((a, b) => a - b);
                          set('business_days', next);
                        }}
                        className={`flex-1 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                          on ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`voice-bh-day-${i}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Outside these hours, inbound callers go straight to voicemail (no ring, no forwarding).
              </p>
            </div>
          </CardContent>
        </Card>

          </TabsContent>
          <TabsContent value="agents" className="space-y-5 mt-0" data-testid="voice-tabcontent-agents">

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Circle size={14} className="fill-violet-500 text-violet-500" /> IVR menu & ring groups</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <ToggleRow
              label="Play a welcome menu (IVR) on inbound calls"
              checked={!!settings.ivr_enabled}
              onChange={(v) => set('ivr_enabled', v)}
              disabled={!canManage}
              testId="voice-ivr-toggle"
            />
            <div className={settings.ivr_enabled ? '' : 'opacity-50 pointer-events-none'}>
              <ToggleRow
                label="Bilingual (ask English / Español first)"
                checked={!!settings.ivr_bilingual}
                onChange={(v) => set('ivr_bilingual', v)}
                disabled={!canManage}
                testId="voice-ivr-bilingual"
              />
              {settings.ivr_bilingual && (
                <div className="mt-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Language prompt</Label>
                  <Input className="mt-1" value={settings.ivr_lang_prompt || ''} onChange={(e) => set('ivr_lang_prompt', e.target.value)} disabled={!canManage} data-testid="voice-ivr-lang-prompt" />
                </div>
              )}
              <Label className="text-xs uppercase tracking-wider text-slate-500 mt-3 block">Greeting (English)</Label>
              <Textarea className="mt-1" rows={2} value={settings.ivr_greeting || ''} onChange={(e) => set('ivr_greeting', e.target.value)} disabled={!canManage} data-testid="voice-ivr-greeting" />
              {settings.ivr_bilingual && (
                <>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mt-3 block">Saludo (Español)</Label>
                  <Textarea className="mt-1" rows={2} value={settings.ivr_greeting_es || ''} onChange={(e) => set('ivr_greeting_es', e.target.value)} disabled={!canManage} data-testid="voice-ivr-greeting-es" />
                </>
              )}

              <div className="mt-4 mb-4 rounded-lg border border-slate-200 p-3">
                <ToggleRow
                  label="Require post-call disposition (default)"
                  checked={settings.disposition_required}
                  onChange={(v) => set('disposition_required', v)}
                  disabled={!canManage}
                  testId="voice-disposition-default-toggle"
                />
                <p className="text-[11px] text-slate-400 mt-1">When on, agents must log a lead status after each call before dismissing the wrap-up panel. A team-level toggle below overrides this for its members.</p>
              </div>

              <div className="mt-4">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Ring groups (teams)</Label>
                <p className="text-[11px] text-slate-400 mt-1">Teams are now managed in the <strong>Teams</strong> card above (with simultaneous / sequential ring strategy). The menu options below can route a keypad digit to any team you create there.</p>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Menu options (keypad)</Label>
                  {canManage && (
                    <Button size="sm" variant="outline" onClick={() => set('ivr_options', [...(settings.ivr_options || []), { digit: String(((settings.ivr_options || []).length % 9) + 1), label: '', action: 'ring', ring_group_id: '', forward_number: '' }])} data-testid="voice-add-ivr-option">+ Add option</Button>
                  )}
                </div>
                <div className="space-y-2 mt-2">
                  {(settings.ivr_options || []).length === 0 && <p className="text-xs text-slate-400">No options yet. Example: press 1 → Sales team, press 9 → Voicemail.</p>}
                  {(settings.ivr_options || []).map((o, oi) => {
                    const upd = (patch) => { const arr = [...settings.ivr_options]; arr[oi] = { ...o, ...patch }; set('ivr_options', arr); };
                    return (
                      <div key={oi} className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-lg p-2" data-testid={`voice-ivr-option-${oi}`}>
                        <Input className="w-14 text-center" maxLength={1} value={o.digit || ''} onChange={(e) => upd({ digit: e.target.value.replace(/\D/g, '').slice(0, 1) })} disabled={!canManage} placeholder="#" data-testid={`voice-ivr-digit-${oi}`} />
                        <Input className="flex-1 min-w-[120px]" value={o.label || ''} onChange={(e) => upd({ label: e.target.value })} disabled={!canManage} placeholder="Label EN (e.g. Sales)" />
                        <Input className="flex-1 min-w-[120px]" value={o.label_es || ''} onChange={(e) => upd({ label_es: e.target.value })} disabled={!canManage} placeholder="Etiqueta ES (ej. Ventas)" />
                        <select className="text-sm border border-slate-200 rounded-lg py-2 px-2 bg-white" value={o.action || 'ring'} onChange={(e) => upd({ action: e.target.value })} disabled={!canManage} data-testid={`voice-ivr-action-${oi}`}>
                          <option value="ring">Ring a team</option>
                          <option value="voicemail">Voicemail</option>
                          <option value="forward">Forward to number</option>
                        </select>
                        {o.action === 'ring' && (
                          <select className="text-sm border border-slate-200 rounded-lg py-2 px-2 bg-white" value={o.ring_group_id || ''} onChange={(e) => upd({ ring_group_id: e.target.value })} disabled={!canManage} data-testid={`voice-ivr-group-${oi}`}>
                            <option value="">All available agents</option>
                            {(settings.ring_groups || []).map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                          </select>
                        )}
                        {o.action === 'forward' && (
                          <Input className="w-40" value={o.forward_number || ''} onChange={(e) => upd({ forward_number: e.target.value })} disabled={!canManage} placeholder="+18135551212" />
                        )}
                        {canManage && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => set('ivr_options', settings.ivr_options.filter((_, i) => i !== oi))}>×</Button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Circle size={14} className="fill-teal-500 text-teal-500" /> Dedicated numbers (per agent)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">Assign a number to an agent (Aircall-style). Their softphone locks to that number, their SMS to leads go out from it, and inbound calls/texts to it reach only them. An agent can hold more than one number.</p>
            <div className="flex justify-end">
              {canManage && (
                <Button size="sm" variant="outline" onClick={() => set('agent_numbers', [...(settings.agent_numbers || []), { user_id: '', number: '', agent_name: '' }])} data-testid="voice-add-assignment">+ Assign number</Button>
              )}
            </div>
            <div className="space-y-2">
              {(settings.agent_numbers || []).length === 0 && <p className="text-xs text-slate-400">No dedicated numbers assigned yet.</p>}
              {(settings.agent_numbers || []).map((an, ai) => {
                const upd = (patch) => { const arr = [...settings.agent_numbers]; arr[ai] = { ...an, ...patch }; set('agent_numbers', arr); };
                return (
                  <div key={ai} className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-lg p-2" data-testid={`voice-assignment-${ai}`}>
                    <select className="text-sm border border-slate-200 rounded-lg py-2 px-2 bg-white min-w-[170px]" value={an.number || ''} onChange={(e) => upd({ number: e.target.value })} disabled={!canManage} data-testid={`voice-assignment-number-${ai}`}>
                      <option value="">Select number…</option>
                      {(settings.caller_numbers || []).map((n) => <option key={n} value={n}>{settings.number_nicknames?.[n] ? `${settings.number_nicknames[n]} — ${n}` : n}</option>)}
                    </select>
                    <span className="text-slate-400 text-sm">→</span>
                    <select className="text-sm border border-slate-200 rounded-lg py-2 px-2 bg-white flex-1 min-w-[180px]" value={an.user_id || ''} onChange={(e) => { const ag = assignAgents.find((x) => x.user_id === e.target.value); upd({ user_id: e.target.value, agent_name: ag?.name || '' }); }} disabled={!canManage} data-testid={`voice-assignment-agent-${ai}`}>
                      <option value="">Select agent…</option>
                      {assignAgents.map((a) => <option key={a.user_id} value={a.user_id}>{a.name} {a.email ? `(${a.email})` : ''}</option>)}
                    </select>
                    {canManage && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => set('agent_numbers', settings.agent_numbers.filter((_, i) => i !== ai))} data-testid={`voice-assignment-del-${ai}`}>×</Button>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Circle size={14} className="fill-rose-500 text-rose-500" /> Recording & access</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <ToggleRow label="Record all calls (dual channel)" checked={settings.recording_enabled} onChange={(v) => set('recording_enabled', v)} disabled={!canManage} testId="voice-recording-toggle" />
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Admins who can access recordings</Label>
              <p className="text-[11px] text-slate-400 mb-2">The KR SuperAdmin can always access recordings. Select any additional admins.</p>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {admins.length === 0 && <span className="text-xs text-slate-400">No KR staff found.</span>}
                {admins.map((a) => (
                  <label key={a.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={(settings.recording_access_user_ids || []).includes(a.user_id)}
                      onCheckedChange={() => toggleAdmin(a.user_id)}
                      disabled={!canManage}
                      data-testid={`voice-rec-access-${a.user_id}`}
                    />
                    <span>{a.display_name || a.name || a.email}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100">
              <ToggleRow
                label="Allow pausing recording during a call"
                checked={settings.pause_recording_enabled}
                onChange={(v) => set('pause_recording_enabled', v)}
                disabled={!canManage}
                testId="voice-pause-recording-toggle"
              />
              <p className="text-[11px] text-slate-400 mt-1 mb-2">
                Lets selected people pause &amp; resume recording mid-call (e.g. while taking a card number). The paused part is skipped, so the recording stays one continuous file.
              </p>
              {settings.pause_recording_enabled && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Roles allowed to pause recording</Label>
                    <div className="grid sm:grid-cols-2 gap-1.5 mt-1">
                      {ROLE_OPTIONS.map((r) => (
                        <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={(settings.pause_recording_roles || []).includes(r.value)}
                            onCheckedChange={() => toggleRole('pause_recording_roles', r.value)}
                            disabled={!canManage}
                            data-testid={`voice-pause-role-${r.value}`}
                          />
                          <span>{r.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Teams allowed to pause recording</Label>
                    <p className="text-[11px] text-slate-400 mb-1">Every member of a selected team can pause &amp; resume recording.</p>
                    <div className="grid sm:grid-cols-2 gap-1.5 mt-1">
                      {(settings.ring_groups || []).length === 0 && <span className="text-xs text-slate-400">No teams yet. Create one under “Teams”.</span>}
                      {(settings.ring_groups || []).map((g) => (
                        <label key={g.group_id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={(settings.pause_recording_team_ids || []).includes(g.group_id)}
                            onCheckedChange={() => toggleRole('pause_recording_team_ids', g.group_id)}
                            disabled={!canManage}
                            data-testid={`voice-pause-team-${g.group_id}`}
                          />
                          <span>{g.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Specific agents allowed to pause recording</Label>
                    <div className="grid sm:grid-cols-2 gap-1.5 mt-1 max-h-48 overflow-auto">
                      {assignAgents.length === 0 && <span className="text-xs text-slate-400">No agents found.</span>}
                      {assignAgents.map((a) => (
                        <label key={a.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={(settings.pause_recording_user_ids || []).includes(a.user_id)}
                            onCheckedChange={() => toggleRole('pause_recording_user_ids', a.user_id)}
                            disabled={!canManage}
                            data-testid={`voice-pause-user-${a.user_id}`}
                          />
                          <span>{a.name || a.email}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck size={16} /> Softphone & log access</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Roles that can use the softphone</Label>
              <p className="text-[11px] text-slate-400 mb-2">KR admins always have it. These roles can make and receive calls too.</p>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {ROLE_OPTIONS.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={(settings.softphone_roles || []).includes(r.value)}
                      onCheckedChange={() => toggleRole('softphone_roles', r.value)}
                      disabled={!canManage}
                      data-testid={`voice-softphone-role-${r.value}`}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Specific agents (uses the general line)</Label>
              <p className="text-[11px] text-slate-400 mb-2">Enable the softphone for an individual agent without turning it on for their whole role. Agents with no dedicated number place calls from the general customer line.</p>
              <div className="grid sm:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1" data-testid="voice-softphone-agents">
                {assignAgents.length === 0 && <span className="text-xs text-slate-400">No agents found.</span>}
                {assignAgents.map((a) => (
                  <label key={a.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={(settings.softphone_user_ids || []).includes(a.user_id)}
                      onCheckedChange={() => toggleRole('softphone_user_ids', a.user_id)}
                      disabled={!canManage}
                      data-testid={`voice-softphone-user-${a.user_id}`}
                    />
                    <span className="truncate">{a.name} {a.email ? <span className="text-slate-400">({a.email})</span> : null}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Roles that can view the Communication Log</Label>
              <p className="text-[11px] text-slate-400 mb-2">KR admins always can. Choose any additional roles.</p>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {ROLE_OPTIONS.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={(settings.comm_log_roles || []).includes(r.value)}
                      onCheckedChange={() => toggleRole('comm_log_roles', r.value)}
                      disabled={!canManage}
                      data-testid={`voice-commlog-role-${r.value}`}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {canManage && (
          <div className="flex justify-end" data-testid="voice-save-inline" />
        )}

          </TabsContent>
          <TabsContent value="history" className="space-y-5 mt-0" data-testid="voice-tabcontent-history">

        <Card>
          <CardHeader><CardTitle className="text-base">Call history &amp; recordings</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                Call history now lives in its own section so you can audit calls and
                search by agent, number, or date.
              </p>
              <Button
                size="sm"
                onClick={() => navigate('/call-history')}
                data-testid="voice-open-call-history"
              >
                <Phone size={14} className="mr-2" /> Open Call History
              </Button>
            </div>
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>

        {canManage && (
          <div className="sticky bottom-0 -mx-1 flex items-center justify-end gap-3 border-t border-slate-200 bg-white/95 backdrop-blur-md px-4 py-3 rounded-xl shadow-[0_-4px_16px_rgba(15,37,71,0.06)] z-30" data-testid="voice-sticky-save">
            <span className="text-xs text-slate-400 mr-auto hidden sm:block">Changes apply across all tabs.</span>
            <Button onClick={save} disabled={saving} className="bg-[#0f2547] hover:bg-[#163666]" data-testid="voice-save-btn">
              {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
              Save settings
            </Button>
          </div>
        )}
      </div>

      <RecordingPlayerDialog
        open={!!playerSid}
        recordingSid={playerSid}
        title="Call recording"
        onClose={() => setPlayerSid(null)}
      />
    </div>
  );
}

function ToggleRow({ label, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
    </div>
  );
}

function TeamsCard({ canManage, onTeamsChange }) {
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // team object or {} for new
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, a] = await Promise.all([voiceAPI.listTeams(), voiceAPI.assignableAgents()]);
      const items = t.data.items || [];
      setTeams(items);
      setAgents(a.data.items || []);
      onTeamsChange && onTeamsChange(items);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load teams'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => setEditing({ name: '', member_user_ids: [], ring_strategy: 'simultaneous', ring_timeout: 20 });
  const openEdit = (t) => setEditing({ ...t });

  const toggleMember = (uid) => setEditing((e) => ({
    ...e,
    member_user_ids: e.member_user_ids.includes(uid)
      ? e.member_user_ids.filter((x) => x !== uid)
      : [...e.member_user_ids, uid],
  }));

  const save = async () => {
    if (!editing.name.trim()) { toast.error('Team name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        member_user_ids: editing.member_user_ids,
        ring_strategy: editing.ring_strategy,
        ring_timeout: Number(editing.ring_timeout) || 20,
      };
      if (editing.group_id) await voiceAPI.updateTeam(editing.group_id, payload);
      else await voiceAPI.createTeam(payload);
      toast.success('Team saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save team'));
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete team "${t.name}"?`)) return;
    try {
      await voiceAPI.deleteTeam(t.group_id);
      toast.success('Team deleted');
      load();
    } catch (e) { toast.error(getErrorMessage(e, 'Delete failed')); }
  };

  return (
    <Card data-testid="voice-teams-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><Users size={16} /> Teams</CardTitle>
        {canManage && (
          <Button size="sm" onClick={openNew} className="bg-[#0f2547]" data-testid="team-new-btn">
            <Plus size={14} className="mr-1" /> New team
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-slate-400">
          Group agents into teams and choose how calls ring them. Use a team in a Call Flow's "Ring group" step. Ring strategy: <strong>Simultaneous</strong> rings everyone at once; <strong>Sequential</strong> rings members one-by-one in order.
        </p>
        {loading && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>}
        {!loading && teams.length === 0 && <p className="text-sm text-slate-500">No teams yet.</p>}
        {teams.map((t) => (
          <div key={t.group_id} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`team-row-${t.group_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800">{t.name}</p>
              <p className="text-xs text-slate-500 truncate">
                {(t.member_names || []).join(', ') || 'No members'}
              </p>
            </div>
            <Badge variant="outline" className="text-[10px] shrink-0">
              {t.ring_strategy === 'sequential' ? 'Sequential' : 'Simultaneous'} · {t.ring_timeout}s
            </Badge>
            <Badge variant="outline" className="text-[10px] shrink-0">{(t.member_user_ids || []).length} member(s)</Badge>
            {canManage && (
              <>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`team-edit-${t.group_id}`}><Pencil size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(t)} data-testid={`team-delete-${t.group_id}`}><Trash2 size={14} /></Button>
              </>
            )}
          </div>
        ))}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Users size={16} /> {editing?.group_id ? 'Edit team' : 'New team'}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">Create or edit a calling team.</DialogDescription>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Team name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Sales Team" data-testid="team-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ring strategy</Label>
                  <Select value={editing.ring_strategy} onValueChange={(v) => setEditing({ ...editing, ring_strategy: v })}>
                    <SelectTrigger className="h-9" data-testid="team-strategy-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simultaneous">Simultaneous (all at once)</SelectItem>
                      <SelectItem value="sequential">Sequential (one by one)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Ring timeout (sec)</Label>
                  <Input type="number" value={editing.ring_timeout} onChange={(e) => setEditing({ ...editing, ring_timeout: e.target.value })} data-testid="team-timeout-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Members</Label>
                <div className="mt-1 border rounded-lg max-h-56 overflow-y-auto divide-y">
                  {agents.map((a) => (
                    <label key={a.user_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`team-member-${a.user_id}`}>
                      <Checkbox checked={editing.member_user_ids.includes(a.user_id)} onCheckedChange={() => toggleMember(a.user_id)} />
                      <span className="truncate">{a.name} <span className="text-slate-400 text-xs">{a.email}</span></span>
                    </label>
                  ))}
                  {agents.length === 0 && <p className="text-sm text-slate-500 px-3 py-2">No eligible users.</p>}
                </div>
                {editing.ring_strategy === 'sequential' && (
                  <p className="text-[11px] text-slate-400 mt-1">Sequential rings members in the order shown above.</p>
                )}
              </div>
              <div className="flex justify-end gap-2 border-t pt-3">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-[#0f2547]" data-testid="team-save-btn">
                  {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Save team
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function NumberHealthCard({ canManage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);

  const check = async () => {
    setLoading(true);
    try {
      const r = await voiceAPI.numberHealth();
      setData(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not check number health'));
    } finally { setLoading(false); }
  };

  const fix = async () => {
    setFixing(true);
    try {
      const r = await voiceAPI.fixNumberHealth();
      toast.success(`Repaired ${r.data.fixed?.length || 0} number(s)`);
      await check();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Repair failed'));
    } finally { setFixing(false); }
  };

  useEffect(() => { check(); /* eslint-disable-next-line */ }, []);

  const nums = data?.numbers || [];
  const anyBad = nums.some((n) => !n.on_twilio || !n.voice_ok);

  return (
    <Card data-testid="voice-number-health-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><HeartPulse size={16} /> Number health</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={check} disabled={loading} data-testid="voice-health-check-btn">
            {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
            Check
          </Button>
          {canManage && (
            <Button size="sm" onClick={fix} disabled={fixing} className="bg-[#0f2547]" data-testid="voice-health-fix-btn">
              {fixing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShieldCheck size={14} className="mr-1" />}
              Fix all
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-slate-400">
          Confirms each number's Twilio webhook points at the CRM (so calls actually reach the flow instead of the carrier's voicemail), and whether a Call Flow is bound.
          {data && !data.twilio_connected && <span className="text-amber-600"> Twilio API not connected here — live status unavailable.</span>}
        </p>
        {nums.length === 0 && !loading && <p className="text-sm text-slate-500">No numbers configured yet.</p>}
        {nums.map((n) => (
          <div key={n.number} className="flex items-center gap-3 py-1.5 border-b last:border-0" data-testid={`health-row-${n.number}`}>
            <span className="font-mono text-sm text-slate-700 w-40 shrink-0">{n.number}</span>
            <span className="text-xs text-slate-500 w-28 shrink-0 truncate">{n.label || '—'}</span>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">{n.kind}</span>
            <div className="flex items-center gap-3 ml-auto text-xs">
              <span className={`flex items-center gap-1 ${n.voice_ok ? 'text-emerald-600' : 'text-rose-600'}`} title={n.current_voice_url || 'no webhook'}>
                {n.voice_ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} Voice
              </span>
              <span className={`flex items-center gap-1 ${n.sms_ok ? 'text-emerald-600' : 'text-amber-600'}`}>
                {n.sms_ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} SMS
              </span>
              {n.flow ? (
                <span className={`flex items-center gap-1 ${n.flow.enabled ? 'text-sky-600' : 'text-slate-400'}`}>
                  <Workflow size={13} /> {n.flow.name}{n.flow.enabled ? '' : ' (off)'}
                </span>
              ) : (
                <span className="text-slate-400 flex items-center gap-1"><Workflow size={13} /> no flow</span>
              )}
            </div>
          </div>
        ))}
        {anyBad && canManage && (
          <p className="text-xs text-amber-700 pt-1">Some numbers aren't pointing at the CRM. Click "Fix all" to repair their Twilio webhooks.</p>
        )}
      </CardContent>
    </Card>
  );
}
